import * as React from "react";
import { Label } from "@radix-ui/react-label";
import styles from "./Textarea.css";

interface TextareaProps {
  name: string;
  value: string;
  onChange: React.ChangeEventHandler<HTMLTextAreaElement>;
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement>;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  maxLength?: number;
  rows?: number;

  variant?: "outlined" | "filled" | "filled-raised" | "bare";
  autoGrow?: boolean;
  "aria-label"?: string;
  "aria-required"?: boolean;
  "aria-describedby"?: string;
}

/**
 * `TextInput`'s multi-line counterpart, with the same API so the two are
 * interchangeable where a field's shape is only known at runtime.
 */
const Textarea = ({
  name,
  value,
  onChange,
  onBlur,
  label,
  placeholder,
  required,
  disabled,
  error,
  maxLength,
  rows = 3,
  variant = "outlined",
  autoGrow = false,
  "aria-label": ariaLabel,
  "aria-required": ariaRequired,
  "aria-describedby": ariaDescribedBy,
}: TextareaProps) => {
  const ref = React.useRef<HTMLTextAreaElement>(null);

  // Resets to `auto` first so the box can shrink again when text is removed.
  // JS rather than `field-sizing: content`, which Firefox doesn't support yet.
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!autoGrow || !el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [autoGrow, value]);

  return (
    <div className={styles.field}>
      {label && (
        <Label htmlFor={name}>
          {label} {required && "*"}
        </Label>
      )}
      <div className={styles.control({ error, variant })}>
        <textarea
          ref={ref}
          className={styles.textarea({ variant, autoGrow })}
          name={name}
          id={name}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          required={required}
          disabled={disabled}
          rows={autoGrow ? 1 : rows}
          aria-label={ariaLabel}
          aria-invalid={error}
          aria-required={ariaRequired}
          aria-describedby={ariaDescribedBy}
          maxLength={maxLength}
        />
      </div>
    </div>
  );
};

export default Textarea;
